import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, MapPin, Menu, Phone, X } from 'lucide-react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useScroll } from '../../hooks/useScroll';
import {
  ACCESSORY_CATEGORIES,
  PART_CATEGORIES,
  STORE_HOME_URL
} from '../../data/storeCatalog';
import { getProductLines } from '../../data/productsLoader';
import { electricBikes } from '../../data/electricBikes';

type NavigationLinkItem = {
  type: 'link';
  label: string;
  href: string;
  external?: boolean;
};

type NavigationDropdownEntry = {
  label: string;
  href: string;
  description?: string;
  external?: boolean;
};

type ProductMenuEntry = {
  label: string;
  href: string;
  description: string;
  image: string;
  meta?: string;
};

type ProductMenuCategory = {
  id: string;
  label: string;
  href: string;
  description: string;
  items: ProductMenuEntry[];
};

type NavigationMegaItem = {
  type: 'mega';
  label: string;
  categories: ProductMenuCategory[];
};

type NavigationDropdownItem = {
  type: 'dropdown';
  label: string;
  items: NavigationDropdownEntry[];
  activePrefixes?: string[];
};

type NavigationItem = NavigationLinkItem | NavigationMegaItem | NavigationDropdownItem;

function getModelCountLabel(count: number) {
  return `${count} modelo${count === 1 ? '' : 's'}`;
}

const productMenuCategories: ProductMenuCategory[] = [
  {
    id: 'scooters',
    label: 'Patinetes electricos',
    href: '/patinetes-electricos',
    description: 'Series infantiles, urbanas, de largo alcance y all-terrain agrupadas por familia Zwheel.',
    items: getProductLines().map((line) => ({
      label: line.name,
      href: `/patinetes-electricos#${line.id}`,
      description: line.description,
      image: line.coverImage,
      meta: getModelCountLabel(line.products.length)
    }))
  },
  {
    id: 'bikes',
    label: 'Bicicletas electricas',
    href: '/bicicletas-electricas',
    description: 'E-bikes con enfoque urbano, confort diario y rutas de mayor autonomia.',
    items: electricBikes.map((bike) => ({
      label: bike.name,
      href: `/bicicletas-electricas#${bike.id}`,
      description: bike.tagline,
      image: bike.heroImage,
      meta: bike.badge
    }))
  },
  {
    id: 'accessories',
    label: 'Accesorios',
    href: '/accesorios-recambios#accessories',
    description: 'Entradas directas por necesidad: seguridad, ruedas, personalizacion y uso diario.',
    items: ACCESSORY_CATEGORIES.map((category) => ({
      label: category.name,
      href: `/accesorios-recambios#${category.id}`,
      description: category.description,
      image: category.image,
      meta: `${category.count} referencias`
    }))
  },
  {
    id: 'parts',
    label: 'Recambios',
    href: '/accesorios-recambios#parts',
    description: 'Piezas clasificadas por sistema para acortar la busqueda de compatibilidad.',
    items: PART_CATEGORIES.map((category) => ({
      label: category.name,
      href: `/accesorios-recambios#${category.id}`,
      description: category.description,
      image: category.image,
      meta: `${category.count} referencias`
    }))
  }
];

const productNavigationItem: NavigationMegaItem = {
  type: 'mega',
  label: 'Productos',
  categories: productMenuCategories
};

const supportNavigationItem: NavigationDropdownItem = {
  type: 'dropdown',
  label: 'Soporte',
  activePrefixes: ['/soporte', '/legal/condiciones-garantia'],
  items: [
    {
      label: 'Condiciones de garantía',
      href: '/legal/condiciones-garantia',
      description: 'Consulta coberturas, exclusiones y el proceso oficial de garantía.'
    },
    {
      label: 'Red oficial de servicio técnico',
      href: '/soporte/red-servicio-tecnico',
      description: 'Encuentra el SAT oficial más cercano o solicita una recogida.'
    },
    {
      label: 'Extensión de garantía 5 años',
      href: 'https://zwheel-shop.com/registra-tu-patinete',
      description: 'Activa la ampliación de garantía oficial para tu patinete.',
      external: true
    },
    {
      label: 'Solicita tu certificado de circulación',
      href: 'https://zwheel-shop.com/solicita-certificado-circulacion',
      description: 'Pide la documentación necesaria para circular con tu patinete.',
      external: true
    }
  ]
};

const matriculasNavigationItem: NavigationDropdownItem = {
  type: 'dropdown',
  label: 'Matrículas',
  activePrefixes: ['/matricula'],
  items: [
    {
      label: 'Matrícula patinetes Zwheel/Mascooter',
      href: 'https://zwheel-shop.com/product/matricula-adhesiva-dgt-oferta-para-zwheel/',
      description: 'Etiqueta identificativa DGT para patinetes Zwheel y Mascooter.',
      external: true
    },
    {
      label: 'Matrícula patinetes de otras marcas',
      href: 'https://zwheel-shop.com/product/matricula-adhesiva-dgt-todos-los-patinetes/',
      description: 'Etiqueta identificativa DGT para VMP de otros fabricantes.',
      external: true
    }
  ]
};

const navigationItems: NavigationItem[] = [
  { type: 'link', label: 'Inicio', href: '/' },
  productNavigationItem,
  { type: 'link', label: 'Tecnología', href: '/technology' },
  supportNavigationItem,
  matriculasNavigationItem,
  { type: 'link', label: 'Sobre Zwheel', href: '/sobre-zwheel' },
  { type: 'link', label: 'Blog', href: '/blog' },
  { type: 'link', label: 'Contacto', href: '/contacto' },
  { type: 'link', label: '¡Contrata tu seguro!', href: 'https://www.segurabici.es/zwheel/', external: true }
];

const accessoryHashes = new Set(ACCESSORY_CATEGORIES.map((category) => `#${category.id}`));
const partHashes = new Set(PART_CATEGORIES.map((category) => `#${category.id}`));

const tiendaItem = {
  label: 'Tienda',
  href: STORE_HOME_URL
};

function isProductsRoute(pathname: string) {
  return pathname === '/patinetes-electricos' ||
    pathname.startsWith('/patinetes-electricos/') ||
    pathname === '/bicicletas-electricas' ||
    pathname.startsWith('/bicicletas-electricas/') ||
    pathname === '/accesorios-recambios' ||
    pathname.startsWith('/accesorios-recambios/');
}

function getCurrentProductCategoryId(pathname: string, hash: string) {
  if (pathname === '/bicicletas-electricas' || pathname.startsWith('/bicicletas-electricas/')) {
    return 'bikes';
  }

  if (pathname === '/accesorios-recambios' || pathname.startsWith('/accesorios-recambios/')) {
    if (hash === '#parts' || partHashes.has(hash)) {
      return 'parts';
    }

    if (hash === '#accessories' || accessoryHashes.has(hash)) {
      return 'accessories';
    }

    return 'accessories';
  }

  return 'scooters';
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopProductsOpen, setDesktopProductsOpen] = useState(false);
  const [desktopOpenDropdownLabel, setDesktopOpenDropdownLabel] = useState<string | null>(null);
  const [activeProductCategoryId, setActiveProductCategoryId] = useState(productMenuCategories[0]?.id ?? 'scooters');
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileOpenDropdownLabel, setMobileOpenDropdownLabel] = useState<string | null>(null);
  const [mobileExpandedProductCategoryId, setMobileExpandedProductCategoryId] = useState<string | null>(productMenuCategories[0]?.id ?? null);
  const location = useLocation();
  const { scrollY } = useScroll();

  const currentProductCategoryId = getCurrentProductCategoryId(location.pathname, location.hash);
  const activeProductCategory = productNavigationItem.categories.find((category) => category.id === activeProductCategoryId)
    ?? productNavigationItem.categories[0];

  useEffect(() => {
    setActiveProductCategoryId(currentProductCategoryId);
    setMobileExpandedProductCategoryId(currentProductCategoryId);
  }, [currentProductCategoryId]);

  const handleDragEnd = (_event: any, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;

    if (offset < -100 || velocity < -500) {
      setMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setMobileProductsOpen(false);
      setMobileOpenDropdownLabel(null);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const transparentHeaderRoutes = [
    '/',
    '/technology',
    '/contact',
    '/contacto',
    '/blog',
    '/soporte',
    '/about',
    '/sobre-zwheel',
    '/legal/cookies',
    '/legal/privacy',
    '/legal/terms',
    '/legal/condiciones-garantia',
    '/matricula',
    '/seguros',
    '/patinetes-electricos',
    '/bicicletas-electricas',
    '/accesorios-recambios'
  ];

  const isTransparentHeaderPage =
    transparentHeaderRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/blog/') ||
    location.pathname.startsWith('/soporte/');

  const sidebarVariants = {
    closed: {
      x: '100%',
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
        mass: 0.8,
      },
    },
    open: {
      x: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
        mass: 0.8,
      },
    },
  };

  const backdropVariants = {
    closed: {
      opacity: 0,
    },
    open: {
      opacity: 1,
    },
  };

  const tiendaButtonClassName = cn(
    'inline-flex h-10 min-w-[108px] items-center justify-center rounded-full px-4 sm:px-5 text-sm font-semibold tracking-[0.01em] text-white transition-all duration-300',
    'bg-[#1677ff] shadow-[0_10px_24px_rgba(22,119,255,0.28)] hover:bg-[#0f6ae6] hover:shadow-[0_14px_30px_rgba(22,119,255,0.34)]',
    scrollY === 0 && isTransparentHeaderPage
      ? 'ring-1 ring-white/10'
      : 'ring-1 ring-[#1677ff]/10'
  );

  const isLinkItemActive = (item: NavigationLinkItem) => {
    if (item.external) {
      return false;
    }

    return item.href === '/blog'
      ? location.pathname === '/blog' || location.pathname.startsWith('/blog/')
      : item.href === '/soporte'
      ? location.pathname === item.href || location.pathname.startsWith('/soporte')
      : item.href === '/sobre-zwheel'
      ? location.pathname === item.href || location.pathname === '/about'
      : item.href === '/contacto'
      ? location.pathname === item.href || location.pathname === '/contact'
      : location.pathname === item.href;
  };

  const isDropdownItemActive = (item: NavigationDropdownItem) =>
    item.activePrefixes?.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)) ?? false;

  const isProductsNavigationActive = isProductsRoute(location.pathname);

  const isProductEntryActive = (href: string) => {
    const [pathname, hash] = href.split('#');

    if (hash) {
      return location.pathname === pathname && location.hash === `#${hash}`;
    }

    return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
  };

  const getDesktopNavTextClass = (isActive: boolean) => cn(
    'relative whitespace-nowrap text-sm font-medium transition-all duration-300 group',
    scrollY === 0
      ? isTransparentHeaderPage
        ? isActive
          ? 'text-white'
          : 'text-white/90 hover:text-white'
        : isActive
        ? 'text-zwheel-black'
        : 'text-zwheel-gray-600 hover:text-zwheel-black'
      : isActive
      ? 'text-black'
      : 'text-gray-700 hover:text-black'
  );

  const closeMenus = () => {
    setDesktopProductsOpen(false);
    setDesktopOpenDropdownLabel(null);
    setMobileMenuOpen(false);
    setMobileOpenDropdownLabel(null);
  };

  return (
    <>
      <header
        className={cn(
          'fixed top-0 w-full z-50 transition-all duration-300',
          scrollY === 0 && isTransparentHeaderPage
            ? 'bg-transparent border-b border-transparent'
            : scrollY === 0
            ? 'bg-white border-b border-gray-200'
            : 'bg-white/90 backdrop-blur-md border-b border-gray-200'
        )}
      >
        <nav className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex h-16 items-center justify-between gap-4 lg:gap-6">
            <div className="flex shrink-0 items-center">
              <Link to="/" className="transition-opacity duration-300">
                <img
                  src="/cropped-Logo-negro-Zwheel_ok.png"
                  alt="ZWHEEL"
                  className={cn(
                    'h-8 md:h-10 w-auto transition-all duration-300',
                    scrollY === 0 && isTransparentHeaderPage
                      ? 'brightness-0 invert'
                      : ''
                  )}
                />
              </Link>
            </div>

            <div className="hidden h-full min-w-0 flex-1 items-center justify-center gap-4 px-4 md:flex lg:gap-6 lg:px-6 xl:px-8">
              {navigationItems.map((item) => {
                if (item.type === 'mega') {
                  const triggerClassName = cn(
                    getDesktopNavTextClass(isProductsNavigationActive || desktopProductsOpen),
                    'flex items-center gap-1.5'
                  );

                  return (
                    <div
                      key={item.label}
                      className="relative flex h-full items-center"
                      onMouseEnter={() => {
                        setDesktopProductsOpen(true);
                        setDesktopOpenDropdownLabel(null);
                      }}
                      onMouseLeave={() => setDesktopProductsOpen(false)}
                    >
                      <button
                        type="button"
                        className={triggerClassName}
                        onFocus={() => {
                          setDesktopProductsOpen(true);
                          setDesktopOpenDropdownLabel(null);
                        }}
                        aria-expanded={desktopProductsOpen}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-300',
                            desktopProductsOpen ? 'rotate-180' : ''
                          )}
                        />
                        <span
                          className={cn(
                            'absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300',
                            isProductsNavigationActive || desktopProductsOpen ? 'w-full' : 'w-0 group-hover:w-full'
                          )}
                        />
                      </button>

                      <AnimatePresence>
                        {desktopProductsOpen && activeProductCategory && (
                          <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className="absolute left-0 top-full z-50 w-[640px] max-w-[calc(100vw-2rem)] pt-3 lg:w-[700px] xl:w-[760px]"
                          >
                            <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-white p-3 shadow-[0_30px_90px_rgba(0,0,0,0.16)]">
                              <div className="grid gap-3 lg:grid-cols-[240px_1fr]">
                                <div className="rounded-[1.5rem] bg-[#f6f2ea] p-2">
                                  <div className="px-3 py-3">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                                      Navegacion de producto
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    {item.categories.map((category) => {
                                      const isActive = activeProductCategory.id === category.id;

                                      return (
                                        <Link
                                          key={category.id}
                                          to={category.href}
                                          onMouseEnter={() => setActiveProductCategoryId(category.id)}
                                          onFocus={() => setActiveProductCategoryId(category.id)}
                                          onClick={() => setDesktopProductsOpen(false)}
                                          className={cn(
                                            'block rounded-[1.25rem] px-4 py-4 transition-all duration-200',
                                            isActive
                                              ? 'bg-white text-gray-900 shadow-[0_12px_30px_rgba(0,0,0,0.08)]'
                                              : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'
                                          )}
                                        >
                                          <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm font-semibold">{category.label}</div>
                                            <ChevronRight className={cn('h-4 w-4 transition-transform', isActive ? 'translate-x-0.5' : '')} />
                                          </div>
                                          <p className="mt-2 text-xs leading-relaxed text-gray-500">
                                            {category.description}
                                          </p>
                                        </Link>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="rounded-[1.5rem] border border-gray-100 bg-white p-6">
                                  <div className="mb-5 flex items-center justify-between gap-4">
                                    <h3 className="text-xl font-bold text-gray-900">
                                      {activeProductCategory.label}
                                    </h3>

                                    <Link
                                      to={activeProductCategory.href}
                                      onClick={() => setDesktopProductsOpen(false)}
                                      className="text-sm font-semibold text-gray-500 transition hover:text-gray-900"
                                    >
                                      Ver todo
                                    </Link>
                                  </div>

                                  <div className="grid gap-3 sm:grid-cols-2">
                                    {activeProductCategory.items.map((entry) => (
                                      <Link
                                        key={entry.href}
                                        to={entry.href}
                                        onClick={() => setDesktopProductsOpen(false)}
                                        className={cn(
                                          'group block rounded-[1.1rem] transition-all duration-200',
                                          isProductEntryActive(entry.href)
                                            ? 'opacity-100'
                                            : 'opacity-92 hover:opacity-100'
                                        )}
                                      >
                                        <div className="overflow-hidden rounded-[1.1rem]">
                                          <div className="aspect-[4/3] overflow-hidden rounded-[1.05rem] bg-[#f4f0e8]">
                                            <img
                                              src={entry.image}
                                              alt={entry.label}
                                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                          </div>
                                          <div className="px-1 pt-3">
                                            <div className="text-sm font-semibold text-gray-900">
                                              {entry.label}
                                            </div>
                                          </div>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                if (item.type === 'dropdown') {
                  const isActive = isDropdownItemActive(item);
                  const isOpen = desktopOpenDropdownLabel === item.label;
                  const triggerClassName = cn(
                    getDesktopNavTextClass(isActive || isOpen),
                    'flex items-center gap-1.5'
                  );

                  return (
                    <div
                      key={item.label}
                      className="relative flex h-full items-center"
                      onMouseEnter={() => {
                        setDesktopOpenDropdownLabel(item.label);
                        setDesktopProductsOpen(false);
                      }}
                      onMouseLeave={() => setDesktopOpenDropdownLabel(null)}
                    >
                      <button
                        type="button"
                        className={triggerClassName}
                        onFocus={() => {
                          setDesktopOpenDropdownLabel(item.label);
                          setDesktopProductsOpen(false);
                        }}
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-300',
                            isOpen ? 'rotate-180' : ''
                          )}
                        />
                        <span
                          className={cn(
                            'absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300',
                            isActive || isOpen ? 'w-full' : 'w-0 group-hover:w-full'
                          )}
                        />
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className="absolute left-0 top-full z-50 w-[380px] max-w-[calc(100vw-2rem)] pt-3"
                          >
                            <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-white p-3 shadow-[0_30px_90px_rgba(0,0,0,0.16)]">
                              <div className="space-y-2">
                                {item.items.map((entry) => (
                                  entry.external ? (
                                    <a
                                      key={entry.href}
                                      href={entry.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setDesktopOpenDropdownLabel(null)}
                                      className="block rounded-[1.35rem] border border-gray-200 bg-white px-5 py-4 transition-all duration-200 hover:border-black/15 hover:bg-[#faf7f1] hover:shadow-[0_14px_35px_rgba(0,0,0,0.05)]"
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm font-semibold text-gray-900">
                                            {entry.label}
                                          </div>
                                          {entry.description && (
                                            <p className="mt-2 text-xs leading-relaxed text-gray-500">
                                              {entry.description}
                                            </p>
                                          )}
                                        </div>
                                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                                      </div>
                                    </a>
                                  ) : (
                                    <Link
                                      key={entry.href}
                                      to={entry.href}
                                      onClick={() => setDesktopOpenDropdownLabel(null)}
                                      className="block rounded-[1.35rem] border border-gray-200 bg-white px-5 py-4 transition-all duration-200 hover:border-black/15 hover:bg-[#faf7f1] hover:shadow-[0_14px_35px_rgba(0,0,0,0.05)]"
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm font-semibold text-gray-900">
                                            {entry.label}
                                          </div>
                                          {entry.description && (
                                            <p className="mt-2 text-xs leading-relaxed text-gray-500">
                                              {entry.description}
                                            </p>
                                          )}
                                        </div>
                                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                                      </div>
                                    </Link>
                                  )
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                const isActive = isLinkItemActive(item);
                const linkClassName = getDesktopNavTextClass(isActive);

                return item.external ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    {item.label}
                    <span
                      className={cn(
                        'absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300',
                        isActive ? 'w-full' : 'w-0 group-hover:w-full'
                      )}
                    />
                  </a>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={linkClassName}
                  >
                    {item.label}
                    <span
                      className={cn(
                        'absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300',
                        isActive ? 'w-full' : 'w-0 group-hover:w-full'
                      )}
                    />
                  </Link>
                );
              })}
            </div>

            <div className="flex shrink-0 items-center space-x-2 sm:space-x-3">
              <a href={tiendaItem.href} className={tiendaButtonClassName} target="_blank" rel="noopener noreferrer">
                {tiendaItem.label}
              </a>

              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'md:hidden transition-colors duration-300',
                  scrollY === 0
                    ? isTransparentHeaderPage ? 'text-white hover:text-white hover:bg-white/10' : 'text-zwheel-gray-600 hover:text-zwheel-black'
                    : 'text-gray-600 hover:text-black'
                )}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
              variants={backdropVariants}
              initial="closed"
              animate={mobileMenuOpen ? 'open' : 'closed'}
              exit="closed"
              transition={{ duration: 0.3, ease: 'easeOut' }}
              onClick={() => setMobileMenuOpen(false)}
            />

            <motion.div
              className="fixed top-0 right-0 h-full w-72 max-w-[85vw] bg-white shadow-2xl z-50 md:hidden border-l border-gray-100"
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">Menú</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto py-6 px-6">
                  <nav className="space-y-3">
                    {navigationItems.map((item, index) => {
                      if (item.type === 'mega') {
                        return (
                          <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.3 }}
                            className="rounded-[1.5rem] border border-black/5 bg-[#faf7f1] p-2"
                          >
                            <button
                              type="button"
                              onClick={() => setMobileProductsOpen((previous) => !previous)}
                              className="flex w-full items-center justify-between rounded-[1rem] px-4 py-3 text-left text-base font-semibold text-gray-900"
                            >
                              <span>Productos</span>
                              <ChevronDown className={cn('h-4 w-4 transition-transform', mobileProductsOpen ? 'rotate-180' : '')} />
                            </button>

                            <AnimatePresence initial={false}>
                              {mobileProductsOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2, ease: 'easeOut' }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-2 pb-1 pt-1">
                                    {item.categories.map((category, categoryIndex) => {
                                      const isExpanded = mobileExpandedProductCategoryId === category.id;

                                      return (
                                        <div
                                          key={category.id}
                                          className={cn(
                                            'py-1.5',
                                            categoryIndex > 0 && 'border-t border-black/5'
                                          )}
                                        >
                                          <div className="flex items-center gap-2">
                                            <Link
                                              to={category.href}
                                              onClick={closeMenus}
                                              className="min-w-0 flex-1 py-1.5 text-sm font-semibold text-gray-900"
                                            >
                                              {category.label}
                                            </Link>
                                            <button
                                              type="button"
                                              onClick={() => setMobileExpandedProductCategoryId(isExpanded ? null : category.id)}
                                              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white hover:text-gray-900"
                                              aria-expanded={isExpanded}
                                            >
                                              <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded ? 'rotate-180' : '')} />
                                            </button>
                                          </div>

                                          <AnimatePresence initial={false}>
                                            {isExpanded && (
                                              <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                                className="overflow-hidden"
                                              >
                                                <div className="mt-1 pb-1">
                                                  {category.items.map((entry, entryIndex) => (
                                                    <Link
                                                      key={entry.href}
                                                      to={entry.href}
                                                      onClick={closeMenus}
                                                      className={cn(
                                                        'block rounded-lg px-3 py-2.5 text-sm transition-colors',
                                                        entryIndex > 0 && 'mt-1 border-t border-black/5 pt-3',
                                                        isProductEntryActive(entry.href)
                                                          ? 'bg-white font-semibold text-gray-900'
                                                          : 'text-gray-700 hover:bg-white hover:text-gray-900'
                                                      )}
                                                    >
                                                      {entry.label}
                                                    </Link>
                                                  ))}
                                                </div>
                                              </motion.div>
                                            )}
                                          </AnimatePresence>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      }

                      if (item.type === 'dropdown') {
                        const isActive = isDropdownItemActive(item);
                        const isOpen = mobileOpenDropdownLabel === item.label;

                        return (
                          <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.3 }}
                            className="rounded-[1.5rem] border border-black/5 bg-[#faf7f1] p-2"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setMobileOpenDropdownLabel((previous) => previous === item.label ? null : item.label);
                                setMobileProductsOpen(false);
                              }}
                              className={cn(
                                'flex w-full items-center justify-between rounded-[1rem] px-4 py-3 text-left text-base font-semibold transition-colors',
                                isActive ? 'text-blue-700' : 'text-gray-900'
                              )}
                            >
                              <span>{item.label}</span>
                              <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen ? 'rotate-180' : '')} />
                            </button>

                            <AnimatePresence initial={false}>
                              {isOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2, ease: 'easeOut' }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-2 pb-1 pt-1">
                                    {item.items.map((entry, entryIndex) => {
                                      const className = cn(
                                        'block rounded-lg px-3 py-2.5 transition-colors hover:bg-white',
                                        entryIndex > 0 && 'mt-1 border-t border-black/5 pt-3'
                                      );

                                      return entry.external ? (
                                        <a
                                          key={entry.href}
                                          href={entry.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={closeMenus}
                                          className={className}
                                        >
                                          <div className="text-sm font-medium text-gray-900">{entry.label}</div>
                                          {entry.description && (
                                            <p className="mt-0.5 text-xs leading-snug text-gray-500">
                                              {entry.description}
                                            </p>
                                          )}
                                        </a>
                                      ) : (
                                        <Link
                                          key={entry.href}
                                          to={entry.href}
                                          onClick={closeMenus}
                                          className={className}
                                        >
                                          <div className="text-sm font-medium text-gray-900">{entry.label}</div>
                                          {entry.description && (
                                            <p className="mt-0.5 text-xs leading-snug text-gray-500">
                                              {entry.description}
                                            </p>
                                          )}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      }

                      const isActive = isLinkItemActive(item);
                      const linkClassName = cn(
                        'relative block text-base font-medium py-3 px-6 rounded-xl transition-all duration-200 active:scale-95 text-center',
                        isActive
                          ? 'text-blue-700'
                          : 'text-gray-700 hover:text-gray-900'
                      );

                      return (
                        <motion.div
                          key={item.href}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.3 }}
                          className="flex justify-center"
                        >
                          {item.external ? (
                            <a
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={closeMenus}
                              className={linkClassName}
                            >
                              {item.label}
                              <span
                                className={cn(
                                  'absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-blue-600 transition-all duration-300 rounded-full',
                                  isActive ? 'w-8' : 'w-0'
                                )}
                              />
                            </a>
                          ) : (
                            <Link
                              to={item.href}
                              onClick={closeMenus}
                              className={linkClassName}
                            >
                              {item.label}
                              <span
                                className={cn(
                                  'absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-blue-600 transition-all duration-300 rounded-full',
                                  isActive ? 'w-8' : 'w-0'
                                )}
                              />
                            </Link>
                          )}
                        </motion.div>
                      );
                    })}
                  </nav>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: navigationItems.length * 0.1, duration: 0.3 }}
                    className="mt-6"
                  >
                    <a
                      href={tiendaItem.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex h-12 w-full items-center justify-center rounded-full bg-[#1677ff] px-6 text-base font-semibold text-white shadow-[0_14px_30px_rgba(22,119,255,0.28)] transition-all duration-300 hover:bg-[#0f6ae6]"
                    >
                      {tiendaItem.label}
                    </a>
                  </motion.div>
                </div>

                <div className="p-6 border-t border-gray-100 space-y-4">
                  <div className="space-y-2 text-center">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      Contacto
                    </h3>
                    <div className="space-y-2 text-sm text-zwheel-gray-600">
                      <div className="flex items-center justify-center space-x-2">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span>info@zwheel.com</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>+34 936 675 437</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span>Calle Riera Roja 12, 08830</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <span className="text-xs">Sant Boi de Llobregat, Barcelona</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-center">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      Síguenos
                    </h3>
                    <div className="flex justify-center space-x-6">
                      <a
                        href="https://es.linkedin.com/company/zwheel"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zwheel-gray-500 hover:text-zwheel-black transition-colors"
                        aria-label="LinkedIn"
                      >
                        <svg aria-hidden="true" className="e-font-icon-svg e-fab-linkedin h-5 w-5" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                          <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
                        </svg>
                      </a>
                      <a
                        href="https://www.youtube.com/@zwheeloficial"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zwheel-gray-500 hover:text-zwheel-black transition-colors"
                        aria-label="YouTube"
                      >
                        <svg aria-hidden="true" className="e-font-icon-svg e-fab-youtube h-5 w-5" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg">
                          <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path>
                        </svg>
                      </a>
                      <a
                        href="https://www.facebook.com/zwheelpatinetes"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zwheel-gray-500 hover:text-zwheel-black transition-colors"
                        aria-label="Facebook"
                      >
                        <svg aria-hidden="true" className="e-font-icon-svg e-fab-facebook h-5 w-5" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                          <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path>
                        </svg>
                      </a>
                      <a
                        href="https://www.instagram.com/_zwheel/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zwheel-gray-500 hover:text-zwheel-black transition-colors"
                        aria-label="Instagram"
                      >
                        <svg aria-hidden="true" className="e-font-icon-svg e-fab-instagram h-5 w-5" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                          <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
                        </svg>
                      </a>
                      <a
                        href="https://www.tiktok.com/@zwheeloficial"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zwheel-gray-500 hover:text-zwheel-black transition-colors"
                        aria-label="TikTok"
                      >
                        <svg aria-hidden="true" className="e-font-icon-svg e-fab-tiktok h-5 w-5" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                          <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"></path>
                        </svg>
                      </a>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      ZWHEEL © 2024 Todos los derechos reservados
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
